/**
 * Shared helpers for displaying team member names and deriving coaching staff.
 * Extracted from TeamsPage.js for testability.
 */

/**
 * Resolve the best display name for a team member (player, coach, etc.).
 * Handles multiple API data shapes: flat fields, nested userId/user objects,
 * and displayName with role suffixes like "Name (Player)".
 */
export function getDisplayName(person) {
  if (!person) return 'Unknown';
  if (typeof person === 'string') return person;
  if (person.fullName) return person.fullName;
  if (person.name) return person.name;
  const first = person.firstName || '';
  const last = person.lastName || '';
  if (first || last) return `${first} ${last}`.trim();
  const user = person.userId || person.user;
  if (user && typeof user === 'object') {
    if (user.fullName) return user.fullName;
    const uFirst = user.firstName || '';
    const uLast = user.lastName || '';
    if (uFirst || uLast) return `${uFirst} ${uLast}`.trim();
  }
  if (person.displayName) {
    const nameOnly = person.displayName.replace(/\s*\([^)]*\)\s*$/, '').trim();
    if (nameOnly) return nameOnly;
  }
  return 'Unknown';
}

/**
 * Derive the list of assistant coaches for a team.
 * Prefers players with roleKey 'assistant-coach' from team.players over the
 * raw team.assistantCoaches array which may be inflated by the backend.
 */
export function getAssistantCoaches(team) {
  const players = team?.players || [];
  const playerRoles = team?.playerRoles || {};
  const asstFromPlayers = players.filter(
    (p) => (p.roleKey || playerRoles[p._id]) === 'assistant-coach'
  );
  return asstFromPlayers.length > 0 ? asstFromPlayers : (team?.assistantCoaches || []);
}

/**
 * Resolve the user ID for a player (handles nested userId objects).
 */
export function getPlayerUserId(player) {
  return player?.userId?._id || player?.userId || player?._id;
}
